import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useAnalyticsTopics — Fetches resolved analytics topic buttons from the backend
 * Topic Resolver API for the BUCI Dynamic Analytics Buttons feature.
 *
 * The backend resolves topics using the Priority Waterfall:
 *   BUCI/CID metadata → SQL Registry patterns → Schema-derived → Fallback
 *
 * @param {string} clientId - Subscriber identifier (subscriber_id)
 * @param {string} productType - Product type string (e.g., "Restaurant.Type-1")
 * @returns {{ topics: Array, loading: boolean, error: string|null, refetch: Function }}
 */
export function useAnalyticsTopics(clientId, productType) {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchTopics = useCallback(async () => {
    // Don't fetch if clientId is missing
    if (!clientId) {
      setTopics([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Cancel any previous in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const params = new URLSearchParams({ subscriber_id: clientId });
      if (productType) {
        params.set('product_type', productType);
      }

      const response = await fetch(
        `/api/analytics-topics?${params.toString()}`,
        { signal: controller.signal }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Request failed with status ${response.status}`);
      }

      const data = await response.json();
      setTopics(data.topics || []);
    } catch (err) {
      if (err.name === 'AbortError') {
        // Request was cancelled — don't update state
        return;
      }
      setError(err.message || 'Failed to fetch analytics topics');
      setTopics([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, productType]);

  // Fetch on mount and when clientId/productType change
  useEffect(() => {
    fetchTopics();

    // Cleanup: abort in-flight request on unmount or dependency change
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetchTopics]);

  return {
    topics,
    loading,
    error,
    refetch: fetchTopics,
  };
}

export default useAnalyticsTopics;
